import styled from '@emotion/styled';
import { WritingAnswerProps } from 'pages/results/component/WritingAnswer/WritingAnswer';
import { Strokes } from 'pages/results/models';
import { forwardRef, useLayoutEffect, useMemo, useRef } from 'react';

export interface WritingAnswerRef {
    replayStrokeDraw: () => void;
}

export type EnWritingAnswerProps = WritingAnswerProps & {
    width: number;
};
const WRITING_CANVAS_VERSION_1 = {
    width: 300,
    height: 150,
};
const WRITING_CANVAS_VERSION_2 = {
    width: 184,
    height: 184,
};
const STROKE_WIDTH_VERSION_1 = 3;
const STROKE_WIDTH_VERSION_2 = 6;
const UNITY_WRITING_CANVAS = 218;
const INTERPOLATION_FACTOR = 10;
export const EnglishWritingAnswer = forwardRef((props: EnWritingAnswerProps, ref) => {
    const { strokes, writingVersion } = props;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const intervalRef = useRef<any>(null);
    let t = 1;

    const replayStrokeDraw = () => {
        if (canvasRef?.current) {
            clearInterval(intervalRef.current);
            const canvas = canvasRef.current;
            const context = canvas?.getContext('2d');
            context?.clearRect(0, 0, canvas?.width, canvas?.height);
            if (outlinePoint.length > 0) {
                const calPoints = calcWaypoints(outlinePoint[0]);
                animate(calPoints, outlinePoint.length, 0);
                t = 1;
            }
        }
    };

    const createOutlinedPoint = (strokes: Strokes[][]) => {
        const scaleFactor = writingVersion ? WRITING_CANVAS_VERSION_2.width / UNITY_WRITING_CANVAS : 0.1;
        const newStrokes = strokes as Strokes[][];
        return newStrokes.map((stroke) =>
            stroke.map((value) => ({
                x: value.x * scaleFactor,
                y: value.y * scaleFactor,
            })),
        );
    };

    const outlinePoint = useMemo(() => createOutlinedPoint(strokes), [strokes]);

    const calcWaypoints = (
        vertices: {
            x: number;
            y: number;
        }[],
    ) => {
        var waypoints = [];
        const tempVertices = vertices as Strokes[];
        for (var i = 1; i < tempVertices.length; i++) {
            var pt0 = tempVertices[i - 1];
            var pt1 = tempVertices[i];
            var dx = pt1.x - pt0.x;
            var dy = pt1.y - pt0.y;
            for (var j = 0; j < 10; j++) {
                var x = pt0.x + (dx * j) / INTERPOLATION_FACTOR;
                var y = pt0.y + (dy * j) / INTERPOLATION_FACTOR;
                waypoints.push({
                    x: x,
                    y: y,
                });
            }
        }
        return waypoints;
    };

    // circle dots instead
    const animate = (
        points: {
            x: number;
            y: number;
        }[],
        strokeLength: number,
        index: number,
    ) => {
        if (canvasRef?.current) {
            const canvas = canvasRef.current;
            let context = canvas?.getContext('2d');
            let t = 1; // Initialize t here
            const pointRadius = 8; // Adjust this value to make the points bigger

            intervalRef.current = setInterval(() => {
                if (t < points?.length) {
                    context?.beginPath();
                    context?.moveTo(points[t - 1].x, points[t - 1].y);
                    context?.lineTo(points[t].x, points[t].y);
                    context?.stroke();

                    // Draw a circle at the current point
                    context?.beginPath();
                    context?.arc(points[t].x, points[t].y, pointRadius, 0, 2 * Math.PI);
                    context?.fill();

                    t++;
                } else {
                    clearInterval(intervalRef.current);
                    t = 1;
                    if (index + 1 < strokeLength) {
                        const calPoints = calcWaypoints(outlinePoint[index + 1]);
                        animate(calPoints, outlinePoint.length, index + 1);
                    }
                }
            }, 10); // Adjust the interval as needed
        }
    };

    // circle dots instead
    const writeStrokes = (
        points: {
            x: number;
            y: number;
        }[],
        context: CanvasRenderingContext2D,
    ) => {
        const pointRadius = 8; // Adjust this value to make the points bigger

        for (let i = 1; i < points.length; i++) {
            if (points[i + 1] && points[i + 1].y) {
                context?.beginPath();
                context?.moveTo(points[i].x, points[i].y);
                context?.lineTo(points[i + 1].x, points[i + 1].y);
                context?.stroke();

                // Draw a circle at the current point
                context?.beginPath();
                context?.arc(points[i].x, points[i].y, pointRadius, 0, 2 * Math.PI);
                context?.fill();
            }
        }
    };

    useLayoutEffect(() => {
        if (canvasRef?.current) {
            const canvas = canvasRef.current;
            const context = canvas?.getContext('2d');
            if (writingVersion) {
                // Version 2 does not require flip Y Axis
                context?.transform(1, 0, 0, 1, 0, 0);
            } else {
                context?.transform(1, 0, 0, -1, 0, canvas.height);
            }
            if (context) {
                context.lineWidth = writingVersion ? STROKE_WIDTH_VERSION_2 : STROKE_WIDTH_VERSION_1;
            }
        }
    }, [canvasRef]);

    useLayoutEffect(() => {
        if (canvasRef?.current) {
            const canvas = canvasRef.current;
            const context = canvas?.getContext('2d');
            if (outlinePoint.length > 0 && context) {
                for (const points of outlinePoint) {
                    const calPoints = calcWaypoints(points);
                    writeStrokes(calPoints, context);
                }
            }
        }
    }, [outlinePoint, canvasRef]);

    return (
        <Container backgroundImage='images/result/lines.png' width={((WRITING_CANVAS_VERSION_2.width / (UNITY_WRITING_CANVAS * 2)) * (props.width ?? 0)).toString() + 'px'}>
            <Canvas width={(WRITING_CANVAS_VERSION_2.width / (UNITY_WRITING_CANVAS * 2)) * (props.width ?? 0)} height={WRITING_CANVAS_VERSION_2.width} ref={canvasRef} onMouseOver={replayStrokeDraw}>
                Canvas
            </Canvas>
        </Container>
    );
});

interface ContainerProps {
    width?: string;
    backgroundImage?: string;
}

const Container = styled.div<ContainerProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${(props) => props.width || '184px'};
    height: 184px;
    border-radius: 8px;
    background: url('/images/result/lines.png');
    background-size: auto 100%;
`;
const Canvas = styled.canvas`
    background: transparent;
`;
